import { GoogleMapsOverlay } from '@deck.gl/google-maps'
import { IconLayer, PathLayer } from '@deck.gl/layers'
import { useMap } from '@vis.gl/react-google-maps'
import { useEffect, useMemo, useRef } from 'react'

import { getSpeedThresholds } from '../../components/InfoWindow/SpeedField'

// --- Color helper ----------------------------------------------------------
export function getColorForSpeed(speed, maxSpeed) {
  if (!maxSpeed || maxSpeed <= 0) return [160, 160, 160, 255] // grey — no data

  const thresholds = getSpeedThresholds(maxSpeed)
  const match = thresholds.find((t) => speed <= t.limit)

  // If within limit, override the green with your preferred neutral teal
  const colorHex =
    match?.limit === maxSpeed
      ? '#3296A0' // teal — "normal driving"
      : match?.color || '#d32f2f' // fallback red

  const hexToRgba = (hex, alpha = 255) => {
    const bigint = parseInt(hex.replace('#', ''), 16)
    return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255, alpha]
  }

  return hexToRgba(colorHex)
}

function getBearing(a, b) {
  const lat1 = (a.lat * Math.PI) / 180
  const lat2 = (b.lat * Math.PI) / 180
  const dLon = ((b.lng - a.lng) * Math.PI) / 180
  const y = Math.sin(dLon) * Math.cos(lat2)
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon)
  const brng = (Math.atan2(y, x) * 180) / Math.PI
  return (brng + 360) % 360
}

const getMidpoint = (a, b) => [(a.position.lng + b.position.lng) / 2, (a.position.lat + b.position.lat) / 2]

function usePathAndArrowData(polylines) {
  return useMemo(() => {
    const pathData = []
    const arrowData = []

    for (const line of polylines || []) {
      const n = line.length
      if (n < 2) continue

      let currentSpeeding = null

      for (let i = 0; i < n; i++) {
        const point = line[i]
        const prev = line[i - 1]
        const next = line[i + 1]

        const { max_speed: maxSpeed, record } = point
        const color = getColorForSpeed(point.speed, maxSpeed)
        const position = [point.position.lng, point.position.lat]

        if (point.speedingData) currentSpeeding = { ...point.speedingData }

        if (currentSpeeding && point.speed > maxSpeed) {
          // Continue speeding propagation
          currentSpeeding = {
            ...currentSpeeding,
            current_speed: point.speed,
            max_speed: maxSpeed,
          }
        } else if (point.speed <= maxSpeed) currentSpeeding = null

        const segmentData = { color, speedingData: currentSpeeding, record }

        const prevMidPoint = prev ? getMidpoint(prev, point) : null
        const nextMidPoint = next ? getMidpoint(point, next) : null

        if (prev) pathData.push({ path: [prevMidPoint, position], ...segmentData })
        if (next) pathData.push({ path: [position, nextMidPoint], ...segmentData })

        // --- Directional arrows every few points ---
        if (i % 5 === 0 && next) {
          arrowData.push({
            position: nextMidPoint,
            color,
            angle: 90 - getBearing(point.position, next.position),
          })
        }
      }
    }

    return { pathData, arrowData }
  }, [polylines])
}

// --- Main component --------------------------------------------------------
export function VehiclePathOverlay({ polylines, setHoverInfo, isInfoWindowOpen }) {
  const map = useMap()
  const overlayRef = useRef(null)

  // Initialize overlay once
  if (!overlayRef.current) {
    overlayRef.current = new GoogleMapsOverlay({ interleaved: true })
  }

  // Attach / detach map
  useEffect(() => {
    if (map) overlayRef.current.setMap(map)
    return () => overlayRef.current.setMap(null)
  }, [map])

  const { pathData, arrowData } = usePathAndArrowData(polylines)

  // Update Deck.gl layers
  useEffect(() => {
    if (!overlayRef.current) return

    const pathLayer = new PathLayer({
      id: 'vehicle-path',
      data: pathData,
      getPath: (d) => d.path,
      getColor: (d) => d.color,
      getWidth: 6,
      widthUnits: 'pixels',
      parameters: { depthTest: false },
      pickable: true,
      onHover: (info) => {
        if (isInfoWindowOpen) return
        setHoverInfo(info?.object ? info : null)
      },
    })

    const arrowLayer = new IconLayer({
      id: 'vehicle-arrows',
      data: arrowData,
      getPosition: (d) => d.position,
      getColor: (d) => d.color,
      getAngle: (d) => d.angle,
      getSize: () => 14,
      sizeUnits: 'pixels',
      sizeScale: 1,
      billboard: true,
      iconAtlas:
        'data:image/svg+xml;charset=utf-8,' +
        encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 10 10">
            <path d="M0,0 L10,5 L0,10 Z" fill="white" />
          </svg>
        `),
      iconMapping: {
        arrow: { x: 0, y: 0, width: 128, height: 128, mask: true },
      },
      getIcon: () => 'arrow',
      parameters: {
        depthTest: false,
        minFilter: 'linear',
        magFilter: 'linear',
      },
    })

    overlayRef.current.setProps({ layers: [pathLayer, arrowLayer], pickingRadius: 10 })
  }, [pathData, arrowData, isInfoWindowOpen])

  return null
}
